
import { Question } from '@/data/types';

export type SubTopic = {
  id: string;
  title: string;
  questionCount: number;
};

// Function to extract subtopics from questions based on their IDs
export const getSubTopicsForTopic = (questions: Question[], topicId: string): SubTopic[] => {
  // Filter questions by the main topic
  const topicQuestions = questions.filter(q => q.topic === topicId);
  
  // Create a map to track subtopics
  const subtopicsMap = new Map<string, { count: number, title: string }>();
  
  if (topicId === 'taj101') {
    // For tajweed questions, extract subtopics from filenames that imported them
    // Check the imports in data/questions.ts for the subtopic names
    
    // Group questions by their ID prefix (e.g., taj001-taj010 = introduction)
    const groupedByPrefix: Record<string, SubTopic> = {
      'introduction': { id: 'introduction', title: 'Introduction', questionCount: 0 },
      'vowels': { id: 'vowels', title: 'Vowels', questionCount: 0 },
      'istiadha-basmalah': { id: 'istiadha-basmalah', title: 'Istiadha & Basmalah', questionCount: 0 },
      'makharij': { id: 'makharij', title: 'Makharij', questionCount: 0 },
      'sifat': { id: 'sifat', title: 'Sifat', questionCount: 0 },
      'tafkhim-tarqiq': { id: 'tafkhim-tarqiq', title: 'Tafkhim & Tarqiq', questionCount: 0 },
      'nun-mim': { id: 'nun-mim', title: 'Nun & Mim Rules', questionCount: 0 },
      'madd': { id: 'madd', title: 'Madd', questionCount: 0 },
      'waqf': { id: 'waqf', title: 'Waqf', questionCount: 0 },
      'practical-examples': { id: 'practical-examples', title: 'Practical Examples', questionCount: 0 },
      'common-mistakes': { id: 'common-mistakes', title: 'Common Mistakes', questionCount: 0 },
      'exceptions': { id: 'exceptions', title: 'Exceptions', questionCount: 0 },
      'additional': { id: 'additional', title: 'Additional Topics', questionCount: 0 },
    };
    
    // Map question IDs to their respective subtopics
    topicQuestions.forEach(question => {
      const id = parseInt(question.id.replace('taj', ''));
      
      // Assign questions to subtopics based on ID ranges
      let subtopic: string;
      if (id <= 10) subtopic = 'introduction';
      else if (id <= 20) subtopic = 'vowels';
      else if (id <= 30) subtopic = 'istiadha-basmalah';
      else if (id <= 40) subtopic = 'makharij';
      else if (id <= 50) subtopic = 'sifat';
      else if (id <= 60) subtopic = 'tafkhim-tarqiq';
      else if (id <= 70) subtopic = 'nun-mim';
      else if (id <= 80) subtopic = 'madd';
      else if (id <= 90) subtopic = 'waqf';
      else if (id <= 100) subtopic = 'practical-examples';
      else if (id <= 110) subtopic = 'exceptions';
      else if (id <= 120) subtopic = 'common-mistakes';
      else subtopic = 'additional';
      
      // Increment the count for this subtopic
      groupedByPrefix[subtopic].questionCount++;
    });
    
    // Convert to an array and filter out empty subtopics
    return Object.values(groupedByPrefix).filter(subtopic => subtopic.questionCount > 0);
  } else {
    // For other topics, use the original approach
    topicQuestions.forEach(question => {
      // The file pattern is in the import, which follows the pattern taj101-subtopic-*
      const idParts = question.id.split('-');
      
      // Skip if not enough parts or the first part doesn't match the topic
      if (idParts.length >= 2) {
        // The subtopic is the second part after splitting by hyphen
        const subtopicId = idParts[1];
        
        if (!subtopicsMap.has(subtopicId)) {
          // Format the subtopic title from the ID (e.g., "makharij" -> "Makharij")
          const title = subtopicId
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
            
          subtopicsMap.set(subtopicId, { count: 1, title });
        } else {
          const existing = subtopicsMap.get(subtopicId)!;
          subtopicsMap.set(subtopicId, { ...existing, count: existing.count + 1 });
        }
      }
    });

    // Convert the map to an array of SubTopic objects
    return Array.from(subtopicsMap.entries()).map(([id, { count, title }]) => ({
      id,
      title,
      questionCount: count
    }));
  }
};

// Get all questions for a specific subtopic
export const getQuestionsForSubTopic = (
  questions: Question[], 
  topicId: string, 
  subtopicId: string
): Question[] => {
  if (topicId === 'taj101') {
    // For taj101, map subtopics based on ID ranges
    return questions.filter(q => {
      if (q.topic !== 'taj101') return false;
      
      const id = parseInt(q.id.replace('taj', ''));
      
      // Check which subtopic this ID falls into
      switch(subtopicId) {
        case 'introduction': return id <= 10;
        case 'vowels': return id > 10 && id <= 20;
        case 'istiadha-basmalah': return id > 20 && id <= 30;
        case 'makharij': return id > 30 && id <= 40;
        case 'sifat': return id > 40 && id <= 50;
        case 'tafkhim-tarqiq': return id > 50 && id <= 60;
        case 'nun-mim': return id > 60 && id <= 70;
        case 'madd': return id > 70 && id <= 80;
        case 'waqf': return id > 80 && id <= 90;
        case 'practical-examples': return id > 90 && id <= 100;
        case 'exceptions': return id > 100 && id <= 110;
        case 'common-mistakes': return id > 110 && id <= 120;
        case 'additional': return id > 120;
        default: return false;
      }
    });
  } else {
    // For other topics, use the original approach
    return questions.filter(q => 
      q.topic === topicId && 
      q.id.includes(`-${subtopicId}`)
    );
  }
};
